<template>
  <div class="screen">
    <div class="map-shell">
      <div ref="map" class="map-canvas"></div>

      <div class="map-title">
        大理市定点医药机构分布图
      </div>

      <form class="map-search" @submit.prevent="applySearch">
        <input
          v-model.trim="searchKeyword"
          class="map-search__input"
          type="text"
          :placeholder="`搜索${activeSourceMeta.label}名称`"
        >
        <button class="map-search__button" type="submit">
          搜索
        </button>
      </form>

      <section class="legend-card">
        <div class="legend-tabs">
          <button
            v-for="item in sourceOptions"
            :key="item.key"
            class="legend-tab"
            :class="{ active: activeSource === item.key }"
            @click="switchSource(item.key)"
          >
            {{ item.label }}
          </button>
        </div>

        <div class="legend-summary">
          <p class="legend-value">{{ totalCount }}</p>
          <p class="legend-label">当前总数</p>
        </div>
      </section>

      <div
        v-for="card in popupCards"
        :key="card.id"
        class="point-popup"
        :style="{
          left: `${card.screen.x}px`,
          top: `${card.screen.y}px`
        }"
      >
        <button class="point-popup__close" @click="closePopup(card.id)">×</button>
        <p class="point-popup__title">名称：{{ card.name }}</p>
        <p>联系电话：{{ card.phone || '-' }}</p>
        <p>地址：{{ card.address || '-' }}</p>
        <p>{{ card.extraLabel || '类型' }}：{{ card.extraValue || card.subtitle || '-' }}</p>
      </div>

      <router-link class="return-home" :to="{ name: 'home' }" aria-label="返回首页">
        <svg viewBox="0 0 120 120" aria-hidden="true">
          <path
            d="M90 28c-17 3-31 12-40 25H28l22 22 22-22H56c7-9 18-15 31-17 6-1 9-1 13-1v-7c-4-1-7-1-10 0z"
            fill="currentColor"
          />
        </svg>
      </router-link>

      <div v-if="loading" class="notice-card">
        正在加载{{ activeSourceMeta.label }}点位...
      </div>

      <div v-if="errorMessage" class="notice-card error">
        <p>{{ errorMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { hasAmapKey, loadAmap, loadAmapPlugins } from '../utils/amap'

const SOURCE_META = {
  institutions: {
    key: 'institutions',
    label: '医疗机构',
    shortLabel: '机构',
    endpoint: '/api/admin/institutions'
  },
  pharmacies: {
    key: 'pharmacies',
    label: '药店',
    shortLabel: '药店',
    endpoint: '/api/admin/pharmacies'
  }
}

const DEFAULT_MARKER_COLOR = '#ef4444'
const PROJECT_CENTER = [100.242888, 25.597114]
const INITIAL_ZOOM = 15.2
const IDLE_RESET_MS = 10000

export default {
  name: 'IndexView',
  data() {
    return {
      loading: true,
      errorMessage: '',
      map: null,
      districtSearch: null,
      districtPolygons: [],
      markerEntries: [],
      points: [],
      totalCount: 0,
      popupCards: [],
      searchKeyword: '',
      activeSource: this.normalizeSource(this.$route.query.source),
      idleTimer: null,
      activityHandler: null
    }
  },
  computed: {
    sourceOptions() {
      return Object.values(SOURCE_META)
    },
    activeSourceMeta() {
      return SOURCE_META[this.activeSource]
    }
  },
  mounted() {
    this.initMap()
  },
  beforeDestroy() {
    this.clearMarkers()
    this.clearIdleTimer()
    this.removeActivityListeners()

    if (this.map) {
      this.map.destroy()
      this.map = null
    }
  },
  methods: {
    normalizeSource(source) {
      return SOURCE_META[source] ? source : 'institutions'
    },
    async initMap() {
      if (!hasAmapKey()) {
        this.loading = false
        this.errorMessage = '没有检测到高德地图 Key。'
        return
      }

      try {
        const AMap = await loadAmap()

        this.map = new AMap.Map(this.$refs.map, {
          center: PROJECT_CENTER,
          zoom: INITIAL_ZOOM,
          resizeEnable: true,
          viewMode: '2D'
        })

        await this.loadDistrictBoundary()
        this.bindMapEvents()
        this.registerActivityListeners()
        await this.fetchPoints()
        this.scheduleIdleReset()
      } catch (error) {
        this.errorMessage = this.getReadableError(error)
      } finally {
        this.loading = false
      }
    },
    async fetchPoints() {
      this.loading = true
      this.errorMessage = ''

      try {
        const { data } = await axios.get(this.activeSourceMeta.endpoint, {
          params: {
            page: 1,
            page_size: 9999,
            ...(this.searchKeyword ? { keyword: this.searchKeyword } : {})
          }
        })

        if (!data || data.code !== 0) {
          throw new Error((data && data.message) || '接口请求失败')
        }

        const payload = data.data || {}
        const list = Array.isArray(payload.list) ? payload.list : []

        this.points = list
          .map(item => this.mapRecord(item, this.activeSource))
          .filter(item => item && Array.isArray(item.position))
        this.totalCount = Number(payload.total) || this.points.length

        this.renderMarkers()
        this.popupCards = []
        this.focusResults()
      } catch (error) {
        this.points = []
        this.totalCount = 0
        this.clearMarkers()
        this.popupCards = []
        this.errorMessage = error.response?.data?.message || error.message || '点位加载失败'
      } finally {
        this.loading = false
      }
    },
    mapRecord(item, source) {
      const longitude = Number(item.longitude)
      const latitude = Number(item.latitude)
      if (Number.isNaN(longitude) || Number.isNaN(latitude)) {
        return null
      }

      if (source === 'institutions') {
        return {
          id: `${source}-${item.id}`,
          source,
          name: item.standard_name,
          subtitle: item.institution_type || '定点医疗机构',
          extraLabel: '机构类型',
          extraValue: item.institution_type || '定点医疗机构',
          phone: item.contact_phone,
          address: item.address,
          position: [longitude, latitude],
          markerColor: DEFAULT_MARKER_COLOR
        }
      }

      return {
        id: `${source}-${item.id}`,
        source,
        name: item.standard_name,
        subtitle: '定点药店',
        extraLabel: '医疗开通范围',
        extraValue: item.medical_scope || '定点药店购药',
        phone: item.contact_phone,
        address: item.address,
        position: [longitude, latitude],
        markerColor: DEFAULT_MARKER_COLOR
      }
    },
    renderMarkers() {
      if (!this.map || !window.AMap) {
        return
      }

      this.clearMarkers()

      this.markerEntries = this.points.map(point => {
        const marker = new window.AMap.Marker({
          position: point.position,
          anchor: 'bottom-left',
          content: this.createMarkerHtml(point),
          offset: new window.AMap.Pixel(-2, -14)
        })

        marker.on('click', () => {
          this.recordActivity()
          this.openPopup(point, marker)
        })

        return { point, marker }
      })

      if (this.markerEntries.length) {
        this.map.add(this.markerEntries.map(item => item.marker))
      }
    },
    clearMarkers() {
      if (!this.map || !this.markerEntries.length) {
        this.markerEntries = []
        return
      }

      this.map.remove(this.markerEntries.map(item => item.marker))
      this.markerEntries = []
    },
    async loadDistrictBoundary() {
      await loadAmapPlugins(['AMap.DistrictSearch'])

      if (typeof window.AMap?.DistrictSearch !== 'function') {
        throw new Error('DistrictSearch 插件不可用')
      }

      this.districtSearch = new window.AMap.DistrictSearch({
        level: 'district',
        subdistrict: 0,
        extensions: 'all',
        showbiz: false
      })

      const district = await new Promise((resolve, reject) => {
        this.districtSearch.search('大理市', (status, result) => {
          if (status !== 'complete' || !result || !result.districtList || !result.districtList.length) {
            reject(new Error('大理市行政边界查询失败'))
            return
          }

          resolve(result.districtList[0])
        })
      })

      const boundaries = district.boundaries || []

      this.districtPolygons.forEach(polygon => this.map.remove(polygon))
      this.districtPolygons = boundaries.map(boundary => {
        const polygon = new window.AMap.Polygon({
          path: boundary,
          strokeColor: '#2f81ff',
          strokeWeight: 3,
          strokeOpacity: 1,
          fillOpacity: 0,
          bubble: true,
          zIndex: 90
        })

        this.map.add(polygon)
        return polygon
      })
    },
    bindMapEvents() {
      if (!this.map || this.__mapEventsBound) {
        return
      }

      const refresh = () => {
        this.recordActivity()
        this.refreshPopupPositions()
      }

      this.map.on('mapmove', refresh)
      this.map.on('zoomchange', refresh)
      this.map.on('resize', refresh)
      this.map.on('click', () => this.recordActivity())
      this.__mapEventsBound = true
    },
    registerActivityListeners() {
      if (this.activityHandler) {
        return
      }

      this.activityHandler = () => this.recordActivity()
      ;['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel'].forEach(eventName => {
        window.addEventListener(eventName, this.activityHandler, { passive: true })
      })
    },
    removeActivityListeners() {
      if (!this.activityHandler) {
        return
      }

      ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel'].forEach(eventName => {
        window.removeEventListener(eventName, this.activityHandler)
      })

      this.activityHandler = null
    },
    recordActivity() {
      this.scheduleIdleReset()
    },
    scheduleIdleReset() {
      this.clearIdleTimer()
      this.idleTimer = window.setTimeout(() => {
        this.popupCards = []
        if (this.searchKeyword) {
          this.searchKeyword = ''
          this.fetchPoints()
          return
        }
        this.resetMapView()
      }, IDLE_RESET_MS)
    },
    clearIdleTimer() {
      if (this.idleTimer) {
        window.clearTimeout(this.idleTimer)
        this.idleTimer = null
      }
    },
    resetMapView() {
      if (!this.map) {
        return
      }

      this.map.setZoomAndCenter(INITIAL_ZOOM, PROJECT_CENTER)
    },
    focusResults() {
      if (!this.map) {
        return
      }

      if (!this.searchKeyword) {
        this.resetMapView()
        return
      }

      if (this.markerEntries.length === 1) {
        this.map.setZoomAndCenter(Math.max(INITIAL_ZOOM, 16.4), this.markerEntries[0].point.position)
        return
      }

      if (this.markerEntries.length > 1) {
        this.map.setFitView(
          this.markerEntries.map(item => item.marker),
          false,
          [130, 130, 130, 130]
        )
        return
      }

      this.resetMapView()
    },
    applySearch() {
      this.recordActivity()
      this.popupCards = []
      this.fetchPoints()
    },
    switchSource(source) {
      if (this.activeSource === source) {
        return
      }

      this.recordActivity()
      this.activeSource = source
      this.popupCards = []
      this.$router.replace({
        name: 'map',
        query: {
          source
        }
      })
      this.fetchPoints()
    },
    openPopup(point) {
      const existingIndex = this.popupCards.findIndex(item => item.id === point.id)
      const card = {
        ...point,
        screen: this.getPopupScreenPosition(point.position)
      }

      if (existingIndex >= 0) {
        this.$set(this.popupCards, existingIndex, card)
      } else {
        this.popupCards.push(card)
      }
    },
    closePopup(id) {
      this.popupCards = this.popupCards.filter(item => item.id !== id)
      this.recordActivity()
    },
    getPopupScreenPosition(position) {
      if (!this.map) {
        return { x: 0, y: 0 }
      }

      const pixel = this.map.lngLatToContainer(position)
      return {
        x: pixel.getX() + 16,
        y: pixel.getY() - 118
      }
    },
    refreshPopupPositions() {
      if (!this.map || !this.popupCards.length) {
        return
      }

      this.popupCards = this.popupCards.map(card => ({
        ...card,
        screen: this.getPopupScreenPosition(card.position)
      }))
    },
    createMarkerHtml(point) {
      return `
        <div class="marker-flag" style="--marker-color:${point.markerColor};">
          <span class="marker-flag__pole"></span>
          <span class="marker-flag__cloth"></span>
        </div>
      `
    },
    getReadableError(error) {
      if (error && error.message === 'missing-key') {
        return '没有检测到高德地图 Key。'
      }

      if (error && error.message) {
        return `高德地图初始化失败：${error.message}`
      }

      return '高德地图初始化失败，请检查 Key、域名白名单和安全密钥配置。'
    }
  }
}
</script>

<style>
.screen,
.map-shell,
.map-canvas {
  width: 100%;
  height: 100%;
}

.map-shell {
  position: relative;
  overflow: hidden;
}

.map-title,
.map-search,
.legend-card,
.notice-card,
.point-popup,
.return-home {
  position: absolute;
  z-index: 2;
}

.legend-card,
.notice-card,
.point-popup {
  border: 1px solid rgba(255, 255, 255, 0.78);
  background: rgba(252, 254, 255, 0.94);
  box-shadow: 0 24px 60px rgba(16, 38, 63, 0.12);
  backdrop-filter: blur(14px);
}

.map-title {
  top: 22px;
  left: 24px;
  z-index: 2;
  color: #0f4fb8;
  font-size: 42px;
  font-weight: 900;
  line-height: 1.08;
  letter-spacing: 0.04em;
  text-shadow: 0 10px 28px rgba(15, 79, 184, 0.18);
}

.map-search {
  top: 24px;
  right: 26px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.82);
  border-radius: 999px;
  background: rgba(252, 254, 255, 0.94);
  box-shadow: 0 24px 60px rgba(16, 38, 63, 0.12);
  backdrop-filter: blur(14px);
}

.map-search__input {
  width: 280px;
  height: 42px;
  padding: 0 18px;
  border: 0;
  border-radius: 999px;
  background: #f3f7fb;
  color: #10263f;
  font-size: 14px;
  outline: none;
}

.map-search__button {
  height: 42px;
  padding: 0 20px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #2f81ff, #5aa0ff);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.legend-card {
  left: 24px;
  bottom: 24px;
  width: 280px;
  padding: 16px;
  border-radius: 24px;
}

.legend-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.legend-tab {
  min-width: 112px;
  height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 999px;
  background: #edf1f7;
  color: #48617d;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.legend-tab.active {
  background: linear-gradient(135deg, #2f81ff, #5aa0ff);
  color: #fff;
}

.legend-summary {
  padding: 18px 16px 14px;
  border-radius: 18px;
  background: linear-gradient(180deg, #f7fbff 0%, #eef4fb 100%);
}

.legend-value {
  margin: 0 0 6px;
  color: #10263f;
  font-size: 46px;
  font-weight: 700;
  line-height: 1;
}

.legend-label {
  margin: 0;
  color: #607289;
  font-size: 14px;
  font-weight: 700;
}

.point-popup {
  width: min(330px, calc(100vw - 48px));
  padding: 16px 18px 14px;
  border-radius: 18px;
  color: #10263f;
}

.point-popup p {
  margin: 0 0 6px;
  font-size: 14px;
  line-height: 1.6;
}

.point-popup__title {
  padding-right: 26px;
  font-size: 16px !important;
  font-weight: 700;
}

.point-popup__close {
  position: absolute;
  top: 8px;
  right: 10px;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #8b9cb0;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

.return-home {
  right: 20px;
  bottom: 18px;
  width: 110px;
  height: 110px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #2f81ff;
  text-decoration: none;
}

.return-home svg {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 8px 16px rgba(47, 129, 255, 0.2));
  transform: rotate(90deg);
}

.notice-card {
  left: 50%;
  bottom: 30px;
  transform: translateX(-50%);
  width: min(560px, calc(100% - 48px));
  padding: 16px 18px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.7;
  text-align: center;
}

.notice-card.error {
  color: #8f2d2d;
  background: rgba(255, 245, 245, 0.94);
}

.marker-flag {
  position: relative;
  width: 26px;
  height: 30px;
}

.marker-flag__pole {
  position: absolute;
  left: 4px;
  bottom: 0;
  width: 3px;
  height: 28px;
  border-radius: 999px;
  background: linear-gradient(180deg, #f9fafb, #d9dde4);
  box-shadow: 0 3px 8px rgba(16, 38, 63, 0.18);
}

.marker-flag__pole::after {
  content: "";
  position: absolute;
  left: -2px;
  bottom: -2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(16, 38, 63, 0.12);
}

.marker-flag__cloth {
  position: absolute;
  top: 1px;
  left: 7px;
  width: 17px;
  height: 12px;
  border-radius: 2px 5px 5px 2px;
  background: var(--marker-color);
  box-shadow: 0 8px 14px rgba(16, 38, 63, 0.18);
  clip-path: polygon(0 0, 100% 8%, 82% 50%, 100% 92%, 0 100%);
  animation: flag-flicker 1.45s ease-in-out infinite;
}

.marker-flag__cloth::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.35), transparent 55%);
}

@keyframes flag-flicker {
  0%,
  100% {
    transform: scale(1) translateY(0);
    opacity: 0.92;
  }

  50% {
    transform: scale(1.05) translateY(-0.5px);
    opacity: 1;
  }
}

@media (max-width: 960px) {
  .map-title {
    top: 16px;
    left: 16px;
    max-width: calc(100% - 32px);
    font-size: 28px;
  }

  .map-search {
    top: 72px;
    left: 16px;
    right: 16px;
  }

  .map-search__input {
    width: auto;
    min-width: 0;
    flex: 1;
  }

  .legend-card,
  .notice-card {
    width: calc(100% - 32px);
  }

  .legend-card {
    left: 16px;
    bottom: 16px;
  }

  .return-home {
    right: 12px;
    bottom: 12px;
    width: 88px;
    height: 88px;
  }
}

@media (max-width: 640px) {
  .map-title {
    font-size: 22px;
  }

  .map-search {
    gap: 8px;
    padding: 8px;
  }

  .map-search__button {
    padding: 0 16px;
  }

  .legend-card {
    width: 240px;
  }

  .legend-tabs {
    flex-direction: column;
  }

  .legend-tab {
    width: 100%;
  }

  .point-popup {
    width: min(300px, calc(100vw - 32px));
  }
}
</style>
